import { Spinner } from '../components/feedback';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { BedrockView } from '@mcpanel/core';
import { api } from '../api/client';
import type { ChangeRequest, ServerView } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { ErrorAlert } from '../components/ui';
import { usePoll } from '../hooks/usePoll';
import { PendingForServer, ReviewDialog } from './ServerManage';

export function BedrockTab({ server }: { server: ServerView }) {
  const result = usePoll(() => api.bedrock(server.id), 10000, [server.id, server.revision]);
  const { canOperate, notify } = useApp();
  const [enabled, setEnabled] = useState(false);
  const [port, setPort] = useState(19132);
  const [busy, setBusy] = useState(false);
  const [request, setRequest] = useState<ChangeRequest>();
  const settings = result.data?.settings;
  useEffect(() => {
    if (settings) {
      setEnabled(settings.enabled);
      setPort(settings.port);
    }
  }, [settings?.enabled, settings?.port]);
  async function plan() {
    setBusy(true);
    try {
      setRequest(await api.createRequest('change_bedrock', { enabled, port }, server.id));
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  }
  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      notify('コピーしました');
    } catch (error) {
      notify(errorMessage(error), true);
    }
  }
  const data: BedrockView | undefined = result.data;
  const supported = data?.support === 'plugin' || data?.support === 'mod';
  return (
    <div className="stack">
      <PendingForServer serverId={server.id} />
      <ErrorAlert error={result.error} />
      {data && (
        <section className="card card-body stack">
          <h3 data-search-id="bedrock">統合版</h3>
          {data.requiresMigration && (
            <p className="alert warning">
              Velocity の統合版を有効にしてから、この設定をもう一度反映してください。ワールドの Geyser を Floodgate
              のみの構成へ切り替えます。
            </p>
          )}
          {data.support === 'needs-paper' && (
            <p>
              Paper に切り替えると使えます（ワールドはそのまま）。<Link to={`?tab=software`}>ソフト・バージョンへ</Link>
            </p>
          )}
          {data.support === 'unsupported' && (
            <p>NeoForge / Forge は対象外です。Mod の内容を Bedrock に変換できません。</p>
          )}
          <div className="field">
            <label className="check">
              <input
                type="checkbox"
                checked={enabled}
                disabled={!canOperate || !supported}
                onChange={(e) => setEnabled(e.target.checked)}
              />{' '}
              統合版を有効にする
            </label>
          </div>
          {server.proxied && (
            <p>
              Velocity（入口）の Geyser を使います。ワールドには Floodgate だけを配置します。
              <Link to="/velocity?tab=bedrock">Velocity の統合版設定へ</Link>
            </p>
          )}
          <div className="field">
            <label htmlFor="bedrock-port">UDP ポート</label>
            <input
              data-search-id="bedrock-port"
              id="bedrock-port"
              className="input"
              type="number"
              min={19132}
              max={19231}
              value={port}
              disabled={!canOperate || server.proxied}
              onChange={(e) => setPort(Number(e.target.value))}
              aria-describedby="bedrock-port-error"
              aria-invalid={!Number.isInteger(port) || port < 19132 || port > 19231}
            />
            <span className="small muted">番号（19132〜19231）</span>
            {(!Number.isInteger(port) || port < 19132 || port > 19231) && (
              <div id="bedrock-port-error" role="alert" style={{ color: 'var(--danger)' }}>
                19132〜19231 の整数を入力してください。
              </div>
            )}
          </div>
          {data.settings.enabled && (
            <div>
              <p>接続先: {data.address}</p>
              <button className="btn" onClick={() => void copy(data.host)}>
                アドレスをコピー
              </button>{' '}
              <button className="btn" onClick={() => void copy(String(data.settings.port))}>
                ポートをコピー
              </button>
            </div>
          )}
          {data.files.map((file) => (
            <div key={file.project}>
              {file.project === 'geyser' ? 'Geyser' : 'Floodgate'}: {file.version}
            </div>
          ))}
          {data.support === 'mod' && <p>Fabric API を Mod に追加してください。</p>}
          {canOperate && (
            <button
              className="btn primary"
              disabled={
                busy ||
                !Number.isInteger(port) ||
                port < 19132 ||
                port > 19231 ||
                (!supported && enabled) ||
                (enabled === settings?.enabled && port === settings?.port && !data.requiresMigration)
              }
              onClick={() => void plan()}
            >
              {busy && <Spinner />}
              変更内容を確認
            </button>
          )}
        </section>
      )}
      {request && <ReviewDialog request={request} onClose={() => setRequest(undefined)} />}
    </div>
  );
}
